import { useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';

const B = { primary: '#4357AD', dark: '#1E2A4A', green: '#D3FEC6', orange: '#FF521B' };

const schema = yup.object({
  tutorId: yup.number().required('Вкажіть репетитора'),
  studentId: yup.number().required('Оберіть учня'),
  startTime: yup
    .mixed()
    .required('Початок обовʼязковий')
    .test('is-valid-date', 'Некоректний час', (value) => Boolean(value) && dayjs(value).isValid()),
  endTime: yup
    .mixed()
    .required('Кінець обовʼязковий')
    .test('is-valid-date', 'Некоректний час', (value) => Boolean(value) && dayjs(value).isValid())
    .test('is-after-start', 'Час завершення має бути після початку', function (value) {
      const { startTime } = this.parent;
      if (!startTime || !value) return true;
      return dayjs(value).isAfter(dayjs(startTime));
    }),
});

const statusOptions = [
  { value: 'SCHEDULED', label: 'Заплановано', color: B.primary, bg: '#EDE9FE' },
  { value: 'COMPLETED', label: 'Проведено', color: '#22C55E', bg: '#D1FAE5' },
  { value: 'RESCHEDULED', label: 'Перенесено', color: '#F59E0B', bg: '#FEF3C7' },
  { value: 'CANCELLED', label: 'Скасовано', color: '#EF4444', bg: '#FEE2E2' },
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
    '& fieldset': { borderColor: '#EDE9FE' },
    '&:hover fieldset': { borderColor: B.primary },
    '&.Mui-focused fieldset': { borderColor: B.primary },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: B.primary },
};

const normalizeScheduleValues = (values = {}) => {
  const start = values.startTime ? dayjs(values.startTime) : dayjs().minute(0).second(0);
  const end = values.endTime ? dayjs(values.endTime) : start.add(1, 'hour');

  return {
    ...values,
    startTime: start,
    endTime: end,
    status: values.status || 'SCHEDULED',
    tutorId: values.tutorId ?? '',
    studentId: values.studentId ?? '',
    comment: values.comment ?? '',
  };
};

const ScheduleFormDialog = ({
  open,
  onClose,
  onSubmit,
  onDelete,
  tutors,
  students,
  initialValues,
  enableTutorSelect = false,
  title,
}) => {
  const formDefaults = useMemo(() => normalizeScheduleValues(initialValues || {}), [initialValues]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: formDefaults,
  });

  useEffect(() => {
    if (open) {
      reset(formDefaults);
    }
  }, [formDefaults, open, reset]);

  const isEditing = Boolean(initialValues?.id);

  const handleFormSubmit = handleSubmit(async (values) => {
    const toLocalDateTime = (value, fallback) =>
      dayjs(value ?? fallback).format('YYYY-MM-DDTHH:mm:ss');

    const resolvedStudentId =
      values.studentId ?? formDefaults.studentId ?? initialValues?.studentId;
    const resolvedTutorId =
      values.tutorId ?? formDefaults.tutorId ?? initialValues?.tutorId;

    const payload = {
      startTime: toLocalDateTime(values.startTime, formDefaults.startTime),
      endTime: toLocalDateTime(values.endTime, formDefaults.endTime),
      status: values.status || formDefaults.status || 'SCHEDULED',
      comment: values.comment ?? formDefaults.comment ?? '',
      studentId: Number(resolvedStudentId),
    };
    if (!isEditing) {
      payload.tutorId = Number(resolvedTutorId);
    }
    await onSubmit(payload);
    onClose();
  });

  const handleDelete = async () => {
    if (!onDelete) return;
    await onDelete();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: '0 20px 60px rgba(67,87,173,0.18)',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)`,
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2.5,
            bgcolor: 'rgba(255,255,255,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <EventIcon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
        <Typography variant="h6" fontWeight={700} sx={{ color: '#fff' }}>
          {title}
        </Typography>
      </Box>

      <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
        <Stack spacing={2.5}>
          {/* Tutor select */}
          {enableTutorSelect && (
            <Controller
              name="tutorId"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Репетитор"
                  select
                  {...field}
                  value={field.value ?? ''}
                  error={Boolean(errors.tutorId)}
                  helperText={errors.tutorId?.message}
                  fullWidth
                  sx={fieldSx}
                >
                  {tutors.map((tutor) => (
                    <MenuItem key={tutor.id} value={tutor.id}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PersonIcon sx={{ fontSize: 18, color: B.primary }} />
                        <span>{tutor.name}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          )}

          {/* Student select */}
          <Controller
            name="studentId"
            control={control}
            render={({ field }) => (
              <TextField
                label="Учень"
                select
                {...field}
                value={field.value ?? ''}
                error={Boolean(errors.studentId)}
                helperText={errors.studentId?.message}
                fullWidth
                sx={fieldSx}
              >
                {students.map((student) => (
                  <MenuItem key={student.id} value={student.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PersonIcon sx={{ fontSize: 18, color: B.primary }} />
                      <span>{student.name}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {/* Time pickers — side by side */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Controller
              name="startTime"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  label="Початок"
                  value={field.value}
                  onChange={field.onChange}
                  ampm={false}
                  format="DD.MM.YYYY HH:mm"
                  minutesStep={10}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(errors.startTime),
                      helperText: errors.startTime?.message,
                      sx: fieldSx,
                    },
                  }}
                />
              )}
            />
            <Controller
              name="endTime"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  label="Завершення"
                  value={field.value}
                  onChange={field.onChange}
                  ampm={false}
                  format="DD.MM.YYYY HH:mm"
                  minutesStep={10}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(errors.endTime),
                      helperText: errors.endTime?.message,
                      sx: fieldSx,
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* Status — chips */}
          <Box>
            <Typography variant="caption" fontWeight={600} sx={{ color: '#8891A5', mb: 1, display: 'block' }}>
              Статус
            </Typography>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {statusOptions.map((option) => {
                    const selected = field.value === option.value;
                    return (
                      <Chip
                        key={option.value}
                        label={option.label}
                        onClick={() => field.onChange(option.value)}
                        sx={{
                          fontWeight: 600,
                          fontSize: 13,
                          px: 0.5,
                          height: 34,
                          borderRadius: 2,
                          border: selected ? `2px solid ${option.color}` : '2px solid #EDE9FE',
                          bgcolor: selected ? option.bg : '#fff',
                          color: selected ? option.color : '#8891A5',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          '&:hover': { borderColor: option.color, bgcolor: option.bg },
                        }}
                      />
                    );
                  })}
                </Stack>
              )}
            />
          </Box>

          {/* Comment */}
          <TextField
            label="Коментар"
            multiline
            minRows={2}
            {...register('comment')}
            fullWidth
            sx={fieldSx}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
        {isEditing && onDelete && (
          <Button
            onClick={handleDelete}
            startIcon={<DeleteOutlineIcon />}
            sx={{
              mr: 'auto',
              color: '#EF4444',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 3,
              '&:hover': { bgcolor: '#FEE2E2' },
            }}
          >
            Видалити
          </Button>
        )}
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: '#8891A5',
            fontWeight: 600,
            borderRadius: 3,
            px: 2.5,
          }}
        >
          Скасувати
        </Button>
        <Button
          variant="contained"
          onClick={handleFormSubmit}
          disabled={isSubmitting}
          sx={{
            textTransform: 'none',
            bgcolor: B.primary,
            '&:hover': { bgcolor: '#3648A0' },
            borderRadius: 3,
            fontWeight: 700,
            px: 3,
            boxShadow: '0 4px 14px rgba(67,87,173,0.3)',
          }}
        >
          Зберегти
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleFormDialog;
